// Shared words and small rules for Resident Notices. Mirrors the server
// (Server/src/services/resident-notice/resident-notice.audience.ts and .copy.ts).

export const NOTICE_TYPES = ["General", "Maintenance", "Fire & Safety", "Rent & Payments", "Inspection", "Parking", "Other"];

export const TYPE_ICON = {
  General: "bell",
  Maintenance: "tool",
  "Fire & Safety": "alert-triangle",
  "Rent & Payments": "dollar-sign",
  Inspection: "clipboard",
  Parking: "truck",
  Other: "file-text",
};

export const MAX_NOTICE_FILES = 5;
export const MAX_NOTICE_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

export const STATUS_META = {
  draft: { label: "Draft", className: "is-draft" },
  scheduled: { label: "Scheduled", className: "is-scheduled" },
  sending: { label: "Sending", className: "is-sending" },
  sent: { label: "Sent", className: "is-sent" },
  cancelled: { label: "Cancelled", className: "is-cancelled" },
};

/** "203" → "2", "1203" → "12", "1-51" → "1", "B2" → "B"; blank → "Other". Same rule as the server. */
export const floorOf = (unitNo) => {
  const u = String(unitNo ?? "").replace(/\s+/g, " ").trim();
  if (!u) return "Other";
  const dash = u.match(/^([A-Za-z0-9]+)\s*[-–]/);
  if (dash) return dash[1].toUpperCase();
  const digits = u.match(/^\d+/);
  if (digits) {
    const d = digits[0];
    if (d.length >= 4) return String(parseInt(d.slice(0, 2), 10));
    return d[0];
  }
  const letters = u.match(/^[A-Za-z]+/);
  if (letters) return letters[0].toUpperCase();
  return "Other";
};

export const formatBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const isAcceptedFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "Floor 2 · 12 units · 15 residents · 2 without email" */
export const audienceSummaryText = (audience, summary) => {
  if (!summary) return "";
  const parts = [];
  const where = describeAudience(audience);
  if (where) parts.push(where);
  parts.push(plural(summary.units || 0, "unit"));
  parts.push(plural(summary.total || 0, "resident"));
  if (summary.without_email) parts.push(`${summary.without_email} without email`);
  return parts.join(" · ");
};

/** "Everyone", "Floors 2, 3", "Units 201 to 210", "3 specific units". */
export const describeAudience = (audience) => {
  const a = audience || {};
  switch (a.mode) {
    case "floors":
      return a.floors?.length ? `Floor${a.floors.length === 1 ? "" : "s"} ${a.floors.join(", ")}` : "Everyone";
    case "range": {
      const from = a.range?.from || "";
      const to = a.range?.to || "";
      if (from && to) return `Units ${from} to ${to}`;
      if (from) return `Units from ${from}`;
      if (to) return `Units up to ${to}`;
      return "Everyone";
    }
    case "units":
      return a.units?.length ? (a.units.length <= 4 ? `Units ${a.units.join(", ")}` : plural(a.units.length, "specific unit")) : "Everyone";
    default:
      return "Everyone";
  }
};

/** A compact date-time for history rows: "22 Aug 2026, 10:42". */
export const formatWhen = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** datetime-local input value for a Date (local time), or "" */
export const toLocalInputValue = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** The empty draft the modal starts from. */
export const emptyNotice = () => ({
  type: "General",
  title: "",
  message: "",
  attachments: [],
  audience: { mode: "all", floors: [], range: { from: "", to: "" }, units: [], include_occupants: false },
  options: { sms: false, scheduled_at: null },
});
